import type { BossAttackDef, EnemyDef } from '@gra/content';
import { hurtPlayer, shoot } from './enemy-ai';
import { nextRandom } from './rng';
import type { Enemy, GameState, Telegraph } from './types';

/** Szarża po zapowiedzi trwa tyle sekund (boss przelatuje cały pas). */
const DASH_TIME = 0.35;

/** Zdrowie zwykłego wroga w tej chwili wyprawy (jak przy zwykłym pojawianiu się). */
export function enemyHealth(s: GameState, def: EnemyDef): number {
  const zone = s.content.zones[s.zoneId]!;
  return def.health * zone.healthMultiplier * (1 + (s.content.game.enemyHealthScalingPerMinute * s.time) / 60) * s.powerPenalty;
}

export function makeEnemy(s: GameState, def: EnemyDef, x: number, y: number, hp: number): Enemy {
  return {
    id: s.nextId++,
    defId: def.id,
    x,
    y,
    hp,
    maxHp: hp,
    speed: def.speed,
    damage: def.damage,
    radius: def.radius,
    xp: def.xp,
    hitFlash: 0,
  };
}

/**
 * Boss z mechanikami (`def.boss`): idzie na bohatera, a gdy atak gotowy — zapowiada go na ziemi
 * i stoi, aż cios spadnie. Ataki idą po kolei z listy. Poniżej progu zdrowia wpada w szał.
 */
export function bossIntent(s: GameState, e: Enemy, def: EnemyDef, dx: number, dy: number, dist: number, dt: number): { vx: number; vy: number } {
  const mech = def.boss!;
  const enrage = mech.enrage;
  if (enrage && !e.enraged && e.hp <= e.maxHp * enrage.at) {
    e.enraged = true;
    e.speed = def.speed * enrage.speed;
    e.summonTimer = 0.6;
    s.events.push({ type: 'bossEnraged', id: e.id, defId: def.id, x: e.x, y: e.y });
  }
  if (e.enraged && enrage?.summon) {
    e.summonTimer = (e.summonTimer ?? 0) - dt;
    if (e.summonTimer <= 0) {
      e.summonTimer = enrage.summon.every;
      summon(s, e, enrage.summon.enemy, enrage.summon.count);
    }
  }

  if (e.dash) {
    e.dash.left -= dt;
    const v = { vx: e.dash.vx, vy: e.dash.vy };
    if (e.dash.left <= 0) delete e.dash;
    return v;
  }
  // W trakcie zapowiedzi boss stoi — widać, skąd padnie cios.
  if (s.telegraphs.some((t) => t.ownerId === e.id)) return { vx: 0, vy: 0 };

  e.timer = (e.timer ?? 2) - dt;
  const range = def.attack?.range ?? 440;
  if (e.timer <= 0 && dist <= range) {
    const attack = mech.attacks[(e.cycle ?? 0) % mech.attacks.length]!;
    e.cycle = (e.cycle ?? 0) + 1;
    e.timer = (def.attack?.interval ?? 3) * (e.enraged ? (enrage?.interval ?? 1) : 1);
    startTelegraph(s, e, attack, dx, dy, dist);
    return { vx: 0, vy: 0 };
  }
  return { vx: (dx / dist) * e.speed * 0.8, vy: (dy / dist) * e.speed * 0.8 };
}

function startTelegraph(s: GameState, e: Enemy, a: BossAttackDef, dx: number, dy: number, dist: number): void {
  const p = s.player;
  const angle = Math.atan2(dy, dx);
  const t: Telegraph = {
    id: s.nextId++,
    ownerId: e.id,
    attack: a.kind,
    shape: 'cast',
    x: e.x,
    y: e.y,
    angle,
    radius: a.radius,
    arc: (a.arc * Math.PI) / 180,
    length: a.length,
    width: a.width,
    damage: a.damage,
    duration: a.windup,
    left: a.windup,
  };
  if (a.kind === 'slam') {
    // Krąg pod bohaterem — trzeba z niego zejść.
    t.shape = 'circle';
    t.x = p.x;
    t.y = p.y;
  } else if (a.kind === 'cone') {
    t.shape = 'cone';
  } else if (a.kind === 'charge') {
    t.shape = 'line';
    t.length = Math.min(a.length, dist + 80);
  }
  s.telegraphs.push(t);
  s.events.push({ type: 'telegraph', id: t.id, ownerId: e.id, shape: t.shape, duration: t.duration });
}

/** Czy punkt (x, y) z promieniem r jest w obszarze zapowiedzi. */
export function insideTelegraph(t: Telegraph, x: number, y: number, r: number): boolean {
  const dx = x - t.x;
  const dy = y - t.y;
  const d = Math.hypot(dx, dy);
  if (t.shape === 'circle') return d <= t.radius + r;
  if (t.shape === 'cone') {
    if (d > t.radius + r) return false;
    if (d <= r) return true;
    let diff = Math.atan2(dy, dx) - t.angle;
    diff = Math.atan2(Math.sin(diff), Math.cos(diff));
    return Math.abs(diff) <= t.arc / 2 + Math.asin(Math.min(1, r / d));
  }
  if (t.shape === 'line') {
    const along = dx * Math.cos(t.angle) + dy * Math.sin(t.angle);
    const across = -dx * Math.sin(t.angle) + dy * Math.cos(t.angle);
    return along >= -r && along <= t.length + r && Math.abs(across) <= t.width / 2 + r;
  }
  return false;
}

/** Odliczanie zapowiedzi; po czasie cios spada (albo leci salwa pocisków). */
export function updateTelegraphs(s: GameState, dt: number): void {
  if (s.telegraphs.length === 0) return;
  const pr = s.content.game.playerRadius;
  for (const t of s.telegraphs) {
    const owner = s.enemies.find((e) => e.id === t.ownerId && e.hp > 0);
    if (!owner) {
      t.left = -1;
      continue;
    }
    t.left -= dt;
    if (t.left > 0) continue;
    const def = s.content.enemies[owner.defId]!;
    if (t.shape === 'cast') {
      castVolley(s, owner, def, t.attack === 'ring');
      continue;
    }
    const hit = insideTelegraph(t, s.player.x, s.player.y, pr);
    if (hit) hurtPlayer(s, t.damage);
    if (t.shape === 'line') {
      // Szarża: boss przelatuje przez pas i ląduje na jego końcu.
      const v = t.length / DASH_TIME;
      owner.dash = { vx: Math.cos(t.angle) * v, vy: Math.sin(t.angle) * v, left: DASH_TIME };
    }
    s.events.push({
      type: 'bossImpact',
      ownerId: owner.id,
      shape: t.shape,
      x: t.x,
      y: t.y,
      angle: t.angle,
      radius: t.radius,
      arc: t.arc,
      length: t.length,
      width: t.width,
      hit,
    });
  }
  s.telegraphs = s.telegraphs.filter((t) => t.left > 0);
}

/** Salwa: pierścień pocisków dookoła (szukaj luki) albo wachlarz w bohatera. */
function castVolley(s: GameState, e: Enemy, def: EnemyDef, ring: boolean): void {
  const atk = def.attack;
  if (!atk) return;
  if (ring) {
    const offset = nextRandom(s) * Math.PI * 2;
    for (let i = 0; i < atk.count; i++) shoot(s, e, def, offset + (i / atk.count) * Math.PI * 2);
  } else {
    const aim = Math.atan2(s.player.y - e.y, s.player.x - e.x);
    const n = e.enraged ? 7 : 5;
    for (let i = 0; i < n; i++) shoot(s, e, def, aim + (i - (n - 1) / 2) * 0.16);
  }
  s.events.push({ type: 'bossCast', id: e.id, x: e.x, y: e.y, ring });
}

function summon(s: GameState, boss: Enemy, enemyId: string, count: number): void {
  const def = s.content.enemies[enemyId];
  if (!def) return;
  const room = s.content.game.spawn.maxEnemies + 20 - s.enemies.length;
  const n = Math.min(count, Math.max(0, room));
  const base = nextRandom(s) * Math.PI * 2;
  for (let i = 0; i < n; i++) {
    const a = base + (i / n) * Math.PI * 2;
    const r = boss.radius + 40 + nextRandom(s) * 30;
    s.enemies.push(makeEnemy(s, def, boss.x + Math.cos(a) * r, boss.y + Math.sin(a) * r, enemyHealth(s, def)));
  }
  if (n > 0) s.events.push({ type: 'bossSummon', id: boss.id, x: boss.x, y: boss.y, count: n });
}
